import React, { ReactNode, useEffect, useState } from "react";
import {
  CDropdown,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CSidebar,
  CSidebarNav,
  CContainer,
  CHeader,
  CNavLink,
  CDropdownDivider,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import SimpleBar from "simplebar-react";
import "simplebar/dist/simplebar.min.css";

import { cilAccountLogout, cilBell, cilMenu, cilOptions, cilUser, cilWallet } from "@coreui/icons";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logoFull from "../assets/icons/logo-full.png";
import "../scss/SideBar.scss";
import AppSidebarNav, { NavItem } from "./AppSidebarNav";
import navigation from "./Nav";
import { getUser, isAdmin, isUserLogin, userLogout } from "../Utils/Auth";
import { UsersItems } from "../models/Users";
import { useAppSelector } from "../hooks/hooks";
import { toggler } from "../redux/selectors";
import { StoreSelectorDropdown } from "./StoreSelectorDropdown";
import { useTheme } from "../hooks/useTheme";
import { ThemeToggle } from "../components/theme/ThemeToggle";
import { useStore } from "../contexts/StoreContext";
import { useFleet } from "../contexts/FleetContext";
import { useSeo } from "../hooks/useSeo";

// Rutas que no están en el menú lateral
const EXTRA_BREADCRUMBS: Record<string, string[]> = {
  "/store/create": ["Tiendas", "Crear tienda"],
  "/store/profile": ["Tienda", "Perfil"],
  "/user/profile": ["Mi usuario", "Perfil"],
  "/products/categories": ["Productos", "Categorías"],
  "/marketing/coupons": ["Promociones", "Cupones"],
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// Migas de pan a partir del menú (Nav.tsx), para que siempre coincidan con lo que ve el usuario
const getBreadcrumbs = (pathname: string, items: NavItem[]): string[] => {
  for (const item of items) {
    if (item.items) {
      const child = item.items.find((sub) => sub.to === pathname);
      if (child) return [item.name, child.name];
    } else if (item.to === pathname) {
      return [item.name];
    }
  }
  if (EXTRA_BREADCRUMBS[pathname]) return EXTRA_BREADCRUMBS[pathname];
  if (pathname.startsWith("/products/edit/")) return ["Productos", "Editar"];
  return pathname
    .split("/")
    .filter((segment) => segment && !/^\d+$/.test(segment))
    .map((segment) => capitalize(segment.replace(/-/g, " ")));
};

interface SideBarProps {
  children: ReactNode;
}

const SideBar: React.FC<SideBarProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<UsersItems>();
  const { storeId, setStoreId, setStoreDetails, stores, refreshStores } = useStore();
  const { companies, refreshCompanies } = useFleet();

  useEffect(() => {
    const current = getUser();
    if (current) {
      setCurrentUser(current);
      // El listado de tiendas vive en StoreContext: se carga aquí una sola vez al
      // montar el Sidebar, y cualquier otro lugar puede refrescarlo con refreshStores()
      refreshStores();
      refreshCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navItemsFiltered = React.useMemo(() => {
    // "Mi flota" solo para usuarios de una empresa de domicilios
    let items = (navigation as NavItem[]).filter((item) => !item.fleet || companies.length > 0);
    if (!isAdmin()) {
      items = items.filter((item) => item.name !== "ADMINISTRACIÓN" && item.name !== "SuperAdmin Panel");
    }
    return items;
  }, [companies.length]);

  if (!isUserLogin()) {
    sessionStorage.setItem("redirect_path", window.location.pathname);
    userLogout();
  }

  const handleSelectStore = (storeId: string) => {
    setStoreId(storeId);
    const selected = stores.find((s) => String(s.id) === String(storeId));
    if (selected) {
      setStoreDetails(selected);
    }
  };

  const handleCreateNewStore = () => navigate("/store/create");
  const handleViewProfile = () => navigate("/store/profile");

  const [sidebarShow, setSidebarShow] = useState(true);
  const { unfoldable } = useAppSelector(toggler);
  const { theme } = useTheme();

  const breadcrumbs = getBreadcrumbs(location.pathname, navItemsFiltered);
  // Pantallas privadas: título en la pestaña y fuera de los buscadores
  useSeo({ title: `${breadcrumbs[breadcrumbs.length - 1] || "Portal"} | DomicilApp` });
  const userName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ");
  const userLabel = currentUser?.email || userName || "Mi usuario";

  return (
    <>
      <CSidebar
        position="fixed"
        unfoldable={unfoldable}
        colorScheme={theme}
        placement="start"
        visible={sidebarShow}
        onVisibleChange={(visible) => setSidebarShow(visible)}
        className="domicilapp-sidebar"
      >
        {/* Marca */}
        <div className="md-sidebar-header md-brand">
          <NavLink to="/dashboard" className="d-inline-flex align-items-center">
            <img src={logoFull} alt="DomicilApp" height={34} />
          </NavLink>
        </div>

        <CSidebarNav>
          <SimpleBar style={{ height: "100%" }}>
            <AppSidebarNav items={navItemsFiltered} />
          </SimpleBar>
        </CSidebarNav>

        {/* Usuario */}
        <div className="md-sidebar-footer">
          <CDropdown direction="dropup" className="w-100">
            <CDropdownToggle caret={false} color="ghost" className="md-menu-row">
              <span className="md-avatar">
                {currentUser?.profile_image_url ? (
                  <img src={currentUser.profile_image_url} alt="" />
                ) : (
                  userLabel.charAt(0)
                )}
              </span>
              <span className="md-menu-row-text">{userLabel}</span>
              <CIcon icon={cilOptions} size="sm" className="md-menu-row-dots" />
            </CDropdownToggle>
            <CDropdownMenu style={{ minWidth: "13rem" }}>
              <CDropdownHeader>{userName || "Mi usuario"}</CDropdownHeader>
              <CDropdownItem onClick={() => navigate("/user/profile")} style={{ cursor: "pointer" }}>
                <CIcon icon={cilUser} size="sm" className="me-2" /> Perfil
              </CDropdownItem>
              <CDropdownItem onClick={() => navigate("/finance/balance")} style={{ cursor: "pointer" }}>
                <CIcon icon={cilWallet} size="sm" className="me-2" /> Billetera
              </CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem onClick={userLogout} style={{ cursor: "pointer" }}>
                <CIcon icon={cilAccountLogout} size="sm" className="me-2" /> Cerrar sesión
              </CDropdownItem>
            </CDropdownMenu>
          </CDropdown>
        </div>
      </CSidebar>

      <div className="wrapper d-flex flex-column min-vh-100">
        <CHeader position="sticky" className="md-topbar p-0">
          <CContainer fluid className="px-3 d-flex align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-2 min-w-0">
              <button
                type="button"
                className="btn md-icon-button"
                title={sidebarShow ? "Ocultar menú" : "Mostrar menú"}
                onClick={() => setSidebarShow(!sidebarShow)}
              >
                <CIcon icon={cilMenu} />
              </button>
              <nav className="md-breadcrumb" aria-label="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <span className="md-breadcrumb-sep">›</span>}
                    <span className={index === breadcrumbs.length - 1 ? "md-breadcrumb-current" : undefined}>{crumb}</span>
                  </React.Fragment>
                ))}
              </nav>
            </div>

            <div className="d-flex align-items-center gap-2">
              {/* Tienda activa (selector) */}
              <StoreSelectorDropdown
                stores={stores}
                activeStoreId={storeId}
                onSelectStore={handleSelectStore}
                onCreateNewStore={handleCreateNewStore}
                onViewProfile={handleViewProfile}
              />
              <ThemeToggle />
              {/* Órdenes por atender */}
              <CNavLink component={NavLink} to="/orders/active" className="btn md-icon-button" title="Órdenes por atender">
                <CIcon icon={cilBell} />
                <span className="md-dot" />
              </CNavLink>
            </div>
          </CContainer>
        </CHeader>

        <div className="body flex-grow-1">{children}</div>

        <footer className="md-footer d-flex justify-content-between">
          <span>DomicilApp Platform &copy; {new Date().getFullYear()}</span>
          <span>Potenciado por DomicilApp Ecosystem</span>
        </footer>
      </div>
    </>
  );
};

export default SideBar;
